import { CodeBlock } from '@/components/CodeBlock'
import { Note } from '@/components/Note'
import { AuditTrail } from '@/hooks/queries/useAuditTrails'
import { downloadAuditTrailsFile } from '@/lib/utils/exports'
import { Button } from '@repo/ui/components'
import { cn } from '@repo/ui/lib/utils'
import { getFormattedTimestamp } from '@repo/utils'
import { CircleAlert, FileDown } from 'lucide-react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { MAX_EVENT_DETAILS_LENGTH } from './constants'
import { getEventDetailsAsJson, isEventDetailsTooLong } from './utils'

type AuditTrailDetailsContentProps = {
  auditTrail: AuditTrail
}

export function AuditTrailDetailsContent(props: AuditTrailDetailsContentProps) {
  const { t } = useTranslation('admin')

  const isTooLong = useMemo(
    () => (props.auditTrail ? isEventDetailsTooLong(props.auditTrail.eventDetails.data) : false),
    [props.auditTrail],
  )

  const jsonContent = useMemo(() => {
    const fullContent = getEventDetailsAsJson(props.auditTrail.eventDetails.data)
    return isTooLong ? fullContent.slice(0, MAX_EVENT_DETAILS_LENGTH) + '\r...' : fullContent
  }, [props.auditTrail.eventDetails.data, isTooLong])

  const handleDownloadAuditTrailsFile = useCallback(() => {
    downloadAuditTrailsFile(
      `${getFormattedTimestamp(props.auditTrail.createdAt)}-${props.auditTrail.createdBy.split('@')[0]}-${props.auditTrail.eventType}-audit-trails`,
      new Blob([getEventDetailsAsJson(props.auditTrail.eventDetails.data)], {
        type: 'application/json',
      }),
    )
  }, [props.auditTrail])

  return (
    <>
      <CodeBlock
        language="json"
        theme="light"
        title={t('auditDetailsSheetContent.title')}
        className={cn(isTooLong ? 'max-h-[62vh]' : 'max-h-[72vh]', 'w-full overflow-auto')}
        code={jsonContent}
        showLineNumbers
      />

      {isTooLong && (
        <Note
          tone="warning"
          note={t('auditDetailsSheetContent.note')}
          icon={CircleAlert}
          action={
            <Button variant="ghost" onClick={handleDownloadAuditTrailsFile}>
              <FileDown />
              {t('auditDetailsSheetContent.export')}
            </Button>
          }
        />
      )}
    </>
  )
}
